import { useQuery } from "vue-query"
import {
  computed,
  reactive,
  toRefs,
  unref,
  useContext,
} from "@nuxtjs/composition-api"

import { ApiPaths } from "@/enums"
import { Coordinates } from "@/types/models/Coordinates"
import { Language } from "@/types/models/Language"
import { CustomQueryHook } from "@/types/vue-query"

export type AutocompleteStructuredFormatting = {
  mainText: string
  secondaryText: string
}

export type AutocompletePrediction = {
  placeId: string
  description: string
  structuredFormatting?: AutocompleteStructuredFormatting
}

export type GeolocationResponse = {
  placeId: string
  description: string
  longitude: string
  latitude: string
}

export interface SearchTermParams {
  searchTerm?: string
}

export interface AutocompleteParams extends SearchTermParams, Coordinates {
  language: Language["id"]
}

export const useAutocompleteQuery: CustomQueryHook<
  AutocompleteParams,
  AutocompletePrediction[]
> = (params, options) => {
  const { $axios } = useContext()

  return useQuery(
    ["autocompleteGeolocationQuery", params],
    async () => {
      const { data } = await $axios.get<AutocompletePrediction[]>(
        ApiPaths.GET_GEOLOCATIONS_AUTOCOMPLETE,
        {
          params: unref(
            computed(() =>
              Object.fromEntries(
                Object.entries(params).filter(([_key, value]) => {
                  return (
                    (typeof value === "string" && !!value) ||
                    (typeof value === "number" && !!value)
                  )
                }),
              ),
            ),
          ),
        },
      )
      return data
    },
    reactive({ keepPreviousData: true, ...(options ? toRefs(options) : {}) }),
  )
}

export const useWorkplacesAutocompleteQuery: CustomQueryHook<
  SearchTermParams,
  GeolocationResponse[]
> = (params, options) => {
  const { $axios } = useContext()

  return useQuery(
    ["autocompleteWorkplacesQuery", params],
    async () => {
      const { data } = await $axios.get<GeolocationResponse[]>(
        ApiPaths.GET_WORKPLACES_AUTOCOMPLETE,
        {
          params: unref(
            computed(() =>
              Object.fromEntries(
                Object.entries(params).filter(([_key, value]) => {
                  return (
                    (typeof value === "string" && !!value) ||
                    (typeof value === "number" && !!value)
                  )
                }),
              ),
            ),
          ),
        },
      )
      return data
    },
    { keepPreviousData: true, ...options },
  )
}

interface GeolocationParams {
  placeIds: string[]
  language: string
}

export const useGeolocationsQuery: CustomQueryHook<
  GeolocationParams,
  GeolocationResponse[]
> = (params, options) => {
  const { $axios } = useContext()

  return useQuery(
    ["getGeolocationsQuery", params],
    async () => {
      const { data } = await $axios.get<GeolocationResponse[]>(
        ApiPaths.GET_GEOLOCATIONS,
        {
          params: {
            placeIds: `${params.placeIds}`,
            language: params.language,
          },
        },
      )
      return data
    },
    { ...options, keepPreviousData: true },
  )
}
